/**
 * English - `careers`: the Careers screen (the roles a shop is hiring for,
 * shown on the storefront's `/careers` pages), its "Show Careers page" switch,
 * and the customiser's settings for the Careers section.
 */
const careers: Record<string, string> = {
  title: 'Careers',
  intro:
    'The roles you are hiring for. Visible roles appear on the Careers page of your storefront and in any Careers section, in this order. They are kept when you switch themes.',
  add: 'Add role',
  loading: 'Loading…',
  load_error: 'This list could not be loaded. Please refresh the page.',
  empty_title: 'No roles yet',
  empty_description: 'Add the first role you are hiring for - a title and a description are enough to start. Job seekers apply on your storefront, through your own link or by email.',
  read_only: 'You can view this list but not change it.',
  visible: 'Shown',
  hidden: 'Hidden',
  closed: 'Closed',
  closes_on: 'Closes {date}',
  applies_by_link: 'Applies through your link',
  applies_by_email: 'Applies by email to {email}',
  no_apply: 'No way to apply set',
  move_up: 'Move up',
  move_down: 'Move down',
  edit: 'Edit',
  delete: 'Delete',
  deleted: 'Role deleted.',
  saved: 'Role saved.',
  generic_error: 'Something went wrong. Please try again.',
  delete_title: 'Delete this role?',
  delete_confirm: 'Delete "{title}"? It disappears from your storefront straight away. To take it down for a while instead, hide it.',

  'feature.title': 'Careers page',
  'feature.label': 'Show Careers page on my storefront',
  'feature.hint_on': 'Your shop has a Careers page listing the roles shown below, and a link to it in your footer.',
  'feature.hint_off': 'Off: your shop has no Careers page, and Careers sections stay hidden. Your roles are kept.',
  'feature.load_error': 'Whether your Careers page is on could not be loaded. Please refresh the page.',
  'feature.turned_on': 'Careers page turned on.',
  'feature.turned_off': 'Careers page turned off.',
  'feature.also_in': 'The same switch is under',
  'feature.storefront_link': 'Storefront › Pages and features',

  'errors.required': 'This field is required.',
  'errors.too_long': 'Keep this to {max} characters or fewer.',
  'errors.url': 'Enter a full web address, starting with https://',
  'errors.email': 'Enter an email address like jobs@yourshop.in',
  'errors.date': 'Pick a date.',

  'employment_type.FULL_TIME': 'Full-time',
  'employment_type.PART_TIME': 'Part-time',
  'employment_type.CONTRACT': 'Contract',
  'employment_type.INTERNSHIP': 'Internship',
  'employment_type.TEMPORARY': 'Temporary',
  'employment_type.VOLUNTEER': 'Volunteer',
  'workplace_type.ON_SITE': 'On-site',
  'workplace_type.REMOTE': 'Remote',
  'workplace_type.HYBRID': 'Hybrid',

  'form.add_title': 'Add role',
  'form.edit_title': 'Edit role',
  'form.cancel': 'Cancel',
  'form.save': 'Save',
  'form.saving': 'Saving…',
  'form.role': 'The role',
  'form.title': 'Job title',
  'form.title_placeholder': 'e.g. Senior stylist',
  'form.department': 'Department',
  'form.department_placeholder': 'e.g. Front desk',
  'form.location': 'Location',
  'form.location_placeholder': 'e.g. Pune',
  'form.employment_type': 'Employment type',
  'form.workplace_type': 'Where the work is done',
  'form.salary_text': 'Pay',
  'form.salary_text_placeholder': 'e.g. ₹25,000 - ₹35,000 a month',
  'form.closes_at': 'Applications close',
  'form.closes_at_hint': 'Leave empty to keep it open. After this day the role leaves your storefront by itself.',
  'form.about': 'About the role',
  'form.summary': 'Short summary',
  'form.summary_hint': 'One or two lines, shown in the list of roles.',
  'form.description': 'Full description',
  'form.description_hint': 'What the job is, who you are looking for, and what you offer. Use headings and lists to make it easy to scan.',
  'form.apply': 'How to apply',
  'form.apply_hint': 'Candidates can also apply through your own form or by email. While the apply form is on, these show as extra options under it; when it is off, the Apply button opens the link if there is one, otherwise it starts an email.',
  'form.apply_url': 'Application link',
  'form.apply_email': 'Application email',
  'form.apply_email_placeholder': 'jobs@yourshop.in',
  'form.is_visible': 'Show on storefront',

  'tips.feature':
    'Adds a Careers page to your shop at /careers, with a page for each role and a link in your footer. Turning it off hides those pages without deleting your roles.',
  'tips.title': 'The name of the job as job seekers search for it.',
  'tips.department': 'The team the role sits in. Optional.',
  'tips.location': 'The town or branch the role is based at. Optional.',
  'tips.employment_type': 'How the role is held - full-time, part-time, an internship and so on.',
  'tips.workplace_type': 'Whether the work is done at your premises, from home, or a mix of both.',
  'tips.salary_text': 'The pay, in your own words. Leave empty to not show it.',
  'tips.closes_at': 'The last day you take applications. The role is hidden from your storefront after it.',
  'tips.summary': 'A line or two that sells the role, shown in the list before a job seeker opens it.',
  'tips.description': 'Everything about the role, shown on its own page.',
  'tips.apply_url': 'A link to a form where people apply, such as a Google Form. Opens in a new tab.',
  'tips.apply_email': 'Where applications are sent by email. Used when there is no application link.',
  'tips.is_visible': 'Hidden roles stay here but do not appear on your storefront.',

  'section.intro':
    'Shows your newest roles with a link to your Careers page. The roles are kept on the Careers screen, where the Careers page is also switched on.',
  'section.manage': 'Manage roles',
  'section.limit': 'Roles to show',
  'section.show_summary': 'Show each role’s summary',
  'section.cta_label': '"View all" link',
  'section.cta_label_placeholder': 'View all openings',
  'tips.section.title': 'The heading above the roles.',
  'tips.section.subtitle': 'An optional line under the heading.',
  'tips.section.limit': 'How many roles to list here, newest first in your order. The rest are on the Careers page.',
  'tips.section.show_summary': 'Shows each role’s short summary under its title.',
  'tips.section.cta_label': 'The words of the link to your Careers page. Leave empty to use the shop’s own.',

  'applies_on_store': 'Applies on your storefront',
  'applies_on_store_resume': 'Applies on your storefront, with a resume',
  'tabs.label': 'Careers sections',
  'tabs.openings': 'Openings',
  'tabs.applications': 'Applications',
  'form.description_placeholder': 'Responsibilities, requirements, benefits…',
  'form.accepts_applications': 'Accept applications on my storefront',
  'form.accepts_applications_on': 'The role’s page has an apply form. Applications arrive under Careers › Applications.',
  'form.accepts_applications_off': 'No apply form. Candidates use the link or email below, if you give one.',
  'form.resume_required': 'Resume required',
  'form.resume_required_hint': 'Candidates must attach a PDF or Word file of up to 5 MB.',
  'tips.accepts_applications': 'Puts a form on the role’s storefront page where candidates send their name, phone, email, a resume and a cover letter. You review them here.',
  'tips.resume_required': 'When on, the form cannot be sent without a resume. When off, a resume is optional.',
  'tips.application_status': 'Where this candidate is in your hiring. Only you see it.',
  'tips.application_notes': 'Your own notes - interview feedback, salary talks. The candidate never sees them.',
  'applications.title': 'Job applications',
  'applications.intro': 'Everyone who applied through the apply form on your storefront, newest first. Open one to contact the candidate, read their resume and move them along.',
  'applications.export': 'Export CSV',
  'applications.exporting': 'Exporting…',
  'applications.export_error': 'The export could not be downloaded. Please try again.',
  'applications.all_roles': 'All roles',
  'applications.role_filter': 'Role',
  'applications.search_placeholder': 'Search name, email or phone',
  'applications.search_label': 'Search applications',
  'applications.status_filter': 'Filter by status',
  'applications.all_statuses': 'All',
  'applications.status.NEW': 'New',
  'applications.status.REVIEWING': 'Reviewing',
  'applications.status.SHORTLISTED': 'Shortlisted',
  'applications.status.INTERVIEW': 'Interview',
  'applications.status.OFFERED': 'Offered',
  'applications.status.HIRED': 'Hired',
  'applications.status.REJECTED': 'Rejected',
  'applications.load_error': 'Applications could not be loaded. Please refresh the page.',
  'applications.empty_title': 'No applications yet',
  'applications.empty_description': 'When someone applies on a role’s page of your storefront, their application appears here.',
  'applications.empty_filtered_title': 'No applications match',
  'applications.empty_filtered_description': 'Try another role, status or search.',
  'applications.col_name': 'Candidate',
  'applications.col_role': 'Role',
  'applications.col_status': 'Status',
  'applications.col_received': 'Received',
  'applications.col_rating': 'Rating',
  'applications.total_one': '{count} application',
  'applications.total_other': '{count} applications',
  'applications.count_one': '{count} application',
  'applications.count_other': '{count} applications',
  'applications.new_count_one': '{count} new',
  'applications.new_count_other': '{count} new',
  'applications.none_for_role': 'No applications yet',
  'applications.unrated': 'Not rated',
  'applications.rating_value': 'Rated {rating} out of 5',
  'applications.rating': 'Rating',
  'applications.rate': 'Rate {rating} out of 5',
  'applications.back': 'All applications',
  'applications.previous': 'Previous',
  'applications.next': 'Next',
  'applications.not_found': 'This application could not be found. It may have been deleted.',
  'applications.load_one_error': 'This application could not be loaded. Please refresh the page.',
  'applications.deleted': 'Application deleted.',
  'applications.applied_for': 'Applied for {role} · {date}',
  'applications.email_action': 'Email',
  'applications.call_action': 'Call',
  'applications.whatsapp_action': 'WhatsApp',
  'applications.field_email': 'Email',
  'applications.field_phone': 'Phone',
  'applications.field_location': 'Current location',
  'applications.field_experience': 'Experience',
  'applications.years_one': '{count} year',
  'applications.years_other': '{count} years',
  'applications.field_linkedin': 'LinkedIn',
  'applications.field_portfolio': 'Portfolio',
  'applications.resume': 'Resume',
  'applications.download_resume': 'Download',
  'applications.open_resume': 'Open',
  'applications.no_resume': 'No resume attached.',
  'applications.resume_error': 'The resume could not be downloaded. Please try again.',
  'applications.cover_letter': 'Cover letter',
  'applications.no_cover_letter': 'No cover letter.',
  'applications.manage': 'Your review',
  'applications.status_label': 'Status',
  'applications.notes': 'Private notes',
  'applications.notes_hint': 'Only you and your team see these.',
  'applications.save_notes': 'Save notes',
  'applications.notes_saved': 'Notes saved.',
  'applications.status_saved': 'Status updated.',
  'applications.delete': 'Delete application',
  'applications.delete_title': 'Delete this application?',
  'applications.delete_confirm': 'The application from {name}, with their resume, is removed for good. This cannot be undone.',
};

export default careers;
